import { MutableRefObject, useEffect, useRef, useState } from 'react'
import { Capabilities } from './model/capabilities'
import {
	hasWindowManagement,
	isExtendedDisplay,
	queryScreenPermission,
	readScreens,
} from './window-management'

type ScreenCapabilities = {
	capabilities: Capabilities
	/** The screens, once the permission allows reading them. */
	screens: MutableRefObject<ScreenDetails | undefined>
}

/**
 * What the browser allows for placing the presentation window, kept
 * current: the permission can change in the site settings, a projector can
 * be plugged in while the page is open. Nothing is watched while `enabled`
 * is false.
 */
export const useScreenCapabilities = (enabled: boolean): ScreenCapabilities => {
	const screens = useRef<ScreenDetails>()
	const [capabilities, setCapabilities] = useState<Capabilities>(() => ({
		windowManagement: hasWindowManagement(),
		extended: isExtendedDisplay(),
		permission: 'unknown',
	}))

	useEffect(() => {
		if (!enabled) {
			return
		}
		let alive = true
		let status: PermissionStatus | undefined
		let watched: ScreenDetails | undefined
		const refreshExtended = (): void =>
			setCapabilities(c => ({ ...c, extended: isExtendedDisplay() }))

		const watchScreens = (details: ScreenDetails | undefined): void => {
			if (!alive || !details || watched === details) {
				return
			}
			watched?.removeEventListener('screenschange', refreshExtended)
			watched = details
			screens.current = details
			details.addEventListener('screenschange', refreshExtended)
		}

		queryScreenPermission().then(result => {
			if (!alive) {
				return
			}
			setCapabilities(c => ({ ...c, permission: result.state }))
			status = result.status
			if (status) {
				const current = status
				current.onchange = () => {
					setCapabilities(c => ({ ...c, permission: current.state }))
					if (current.state === 'granted') {
						readScreens().then(watchScreens)
					}
				}
			}
			if (result.state === 'granted') {
				readScreens().then(watchScreens)
			}
		})

		window.addEventListener('focus', refreshExtended)
		return () => {
			alive = false
			if (status) {
				status.onchange = null
			}
			watched?.removeEventListener('screenschange', refreshExtended)
			window.removeEventListener('focus', refreshExtended)
		}
	}, [enabled])

	return { capabilities, screens }
}
